import { useState, useEffect, useRef } from "react";
import { Box, Popover, Dialog, MenuIcon, BookmarkIcon, BookmarkBorderIcon, ShareButton, IconButton } from "src/components";
import { useIsMobile, useSnackbar } from 'src/hooks';
import TableOfContentsPopup from './table-of-contents-popup';
import { updateUserBookmarks, useAuth } from "src/utils";
import { useNavigate } from "react-router-dom";
import { DesktopProfileModal } from "src/pages/profile";

export default function ArticleOverlayControlButtons({articleId, tableOfContents, visible}) {
    const [loading, setLoading] = useState(false);
    const [chaptersAnchorEl, setChaptersAnchorEl] = useState(null);
    const [bookmarkIds, setBookmarkIds] = useState([]);
    const [displayLogin, setDisplayLogin] = useState(false);
    
    const isMobile = useIsMobile();
    const navigate = useNavigate();
    const timer = useRef();
    const { user, setUser } = useAuth();
    const { openSnackbar } = useSnackbar();

    useEffect(() => {
        return () => {
            clearTimeout(timer.current);
        };
    }, []);

    useEffect(() => {
        if (user === null) {
            setBookmarkIds([]);
            return;
        }
        setBookmarkIds(user.bookmarks || []);
    }, [user]);

    const handleBookmarkClick = async () => {
        if (!user) {
            isMobile ? navigate("/profile") : setDisplayLogin(true);
            return;
        }

        setLoading(true);

        timer.current = window.setTimeout(async () => {
            try {
                const updatedBookmarks = await updateUserBookmarks(articleId);
                setBookmarkIds(updatedBookmarks);
                setUser((prev) => ({ ...prev, bookmarks: updatedBookmarks }));
            } catch (err) {
                console.error("Error toggling bookmark", err);
                openSnackbar("Error saving bookmark", 'error');
            } finally {
                setLoading (false);
            }
        }, 500); 
    };

    const handleChaptersClick = (e) => {
        if (isMobile) {
            setChaptersAnchorEl(e.currentTarget);
        } else {
            setChaptersAnchorEl(e.currentTarget);
        }
    }

    const handleContentsMenuItemClick = (id) => {
        const element = document.getElementById(id);
        if (element) {
            element.scrollIntoView({ behavior: "smooth", block: "start" });
        }
        setChaptersAnchorEl(null);
    };

    return (<>
        <Box
            sx={{
                position: 'fixed',
                display: 'flex',
                flexDirection: 'column',
                bottom: isMobile ? 75 : 24,
                right: 24,
                zIndex: 1300,
                borderRadius: 100,
                paddingY: 1,
                gap: 0.5,
                transform: visible ? 'translateX(0)' : 'translateX(120%)',
                opacity: visible ? 1 : 0,
                transition: 'transform 0.4s ease, opacity 0.4s ease'
        }}>

            <IconButton
                disableRipple
                size="small"
                sx={{
                    color: 'primary.main', 
                    bgcolor: '#fff', 
                    width: 40, 
                    height: 40, 
                    boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
                    '&:active': {
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                        transform: 'scale(0.97)',
                    }
                }}
                onClick={handleBookmarkClick}
                loading={loading}>

                {bookmarkIds.includes(articleId) ? (
                    <BookmarkIcon />
                ) : (
                    <BookmarkBorderIcon />
                )}
            </IconButton>

            <IconButton 
                disableRipple 
                size="small"
                sx={{
                    color: 'primary.main', 
                    bgcolor: '#fff', 
                    width: 40, 
                    height: 40, 
                    boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
                    '&:hover': {
                        bgcolor: 'grey.100',
                        boxShadow: '0 4px 10px rgba(0,0,0,0.2)',
                        transform: 'scale(1.05)',
                    },
                    '&:active': {
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                        transform: 'scale(0.97)',
                    },
                }}
                onClick={handleChaptersClick}>
                <MenuIcon />
            </IconButton>

            <ShareButton
                size="small"
                iconSize="large"
                iconColor="#fff"
                sx={{ bgcolor: 'primary.main' }}
            />

            {/* Desktop Popover */}
            <Popover
                open={Boolean(chaptersAnchorEl)}
                anchorEl={chaptersAnchorEl}
                onClose={() => setChaptersAnchorEl(null)}
                anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                }}
                transformOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
            >
                <Box className="scrollBox" sx={{ p: 2, width: 300, maxHeight: '350px' }}>
                    <TableOfContentsPopup
                        contents={tableOfContents}
                        onClick={handleContentsMenuItemClick}
                        onClose={() => setChaptersAnchorEl(null)}
                    />
                </Box>
            </Popover>

            {/* Mobile Dialog */}
            {/*<Dialog
                open={chaptersDialogOpen}
                onClose={() => setChaptersDialogOpen(false)}
                fullScreen
            >
                <Box sx={{ p: 2 }}>
                    <TableOfContentsPopup
                        contents={tableOfContents}
                        onClick={handleContentsMenuItemClick}
                        onClose={() => setChaptersDialogOpen(false)}
                    />
                </Box>
            </Dialog>*/}

        </Box>

        <DesktopProfileModal
            displayLogin={displayLogin}
            setDisplayLogin={setDisplayLogin}
        />

        </>
    );
}
